/**
 * Make properties, whose keys are in K, required and everything else optional
 */
export type PartialRequired<T, K extends keyof T> = Partial<T> & {
  [P in K]-?: T[P];
};

/**
 * Alias type for empty object.
 * (Use {@link Record} instead of `{}` as suggested by ESLint `ban-types` rule)
 */
export type EmptyObject = Record<string, never>;

/**
 * Same as {@link Extract} but will not compile if {@link U} does not extend {@link T}
 */
export type StrictExtract<T, U extends T> = U;

/**
 * Same as {@link Omit} but will not compile if {@link K} does not extend key of {@link T}
 */
export type StrictOmit<T, K extends keyof T> = Omit<T, K>;

/**
 * Helper function to check if {@link prop} is in {@link obj} and return type predicate
 *
 * @param obj
 * @param prop
 */
export const hasProperty = <T extends object, K extends PropertyKey>(obj: T, prop: K): obj is T & Record<K, unknown> => prop in obj;

/**
 * Mark some properties as optional
 */
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Typed version of `Object.keys`
 */
export const objectKeys = Object.keys as <T>(o: T) => Extract<keyof T, string>[];
